/**
 * Onboarding de ativação: modal de boas-vindas, tour acionável e o checklist
 * "Primeiros Passos".
 *
 * `checklist.items` é indexado pela ChecklistKey e `checklist.groups` pelos
 * grupos de processos do PMBoK. As chaves nunca mudam com o idioma: elas são o
 * que persiste em localStorage e o que os módulos emitem no evento de ação.
 */
const onboarding = {
  welcome: {
    title: 'Bem-vindo ao MeuGantt',
    subtitle: 'Duas respostas rápidas e montamos um cronograma pronto para você ajustar.',
    roleQuestion: 'Qual é o seu papel?',
    typeQuestion: 'Que tipo de projeto você vai tocar primeiro?',
    skip: 'Pular por agora',
    start: 'Montar meu cronograma',
    roles: {
      project_manager: 'Gerente de Projetos',
      pmo: 'PMO',
      product_owner: 'Product Owner',
      coordinator: 'Coordenador',
      other: 'Outro',
    },
    projectTypes: {
      software: { label: 'Software', description: 'Desenvolvimento de produto ou sistema' },
      website: { label: 'Site ou app', description: 'Site institucional, landing ou aplicativo' },
      marketing: { label: 'Marketing', description: 'Campanha, lançamento ou conteúdo' },
      evento: { label: 'Evento', description: 'Evento, congresso ou feira' },
      implantacao: { label: 'Implantação', description: 'Implantação, rollout ou operação' },
    },
  },

  tour: {
    back: 'Voltar',
    close: 'Fechar',
    last: 'Concluir',
    next: 'Próximo',
    nextWithProgress: 'Próximo ({current} de {total})',
    open: 'Abrir',
    skip: 'Pular tour',
    steps: {
      editTask: {
        title: 'Ajuste para a sua realidade',
        body: 'Clique em qualquer tarefa para renomear, mudar datas ou duração. O caminho crítico recalcula sozinho.',
      },
      setDates: {
        title: 'Defina os prazos',
        body: 'Arraste as barras para mudar início e término de cada tarefa. A folga e o caminho crítico recalculam na hora.',
      },
      addSubtask: {
        title: 'Detalhe a entrega',
        body: 'Clique em Adicionar tarefa para incluir uma atividade. Para criar uma subtarefa, selecione a linha e pressione Tab.',
      },
      addDependency: {
        title: 'Conecte e veja o caminho crítico',
        body: 'Ligue as tarefas por dependência (as setas no gráfico) e ative o Caminho Crítico. As barras em vermelho são o que não pode atrasar sem empurrar a entrega.',
      },
      assignOwner: {
        title: 'Defina os responsáveis',
        body: 'Atribua um responsável a cada tarefa na coluna Responsável. Use este filtro para ver o que é de cada parte interessada.',
      },
      kanbanView: {
        title: 'Outra visão do mesmo plano',
        body: 'O mesmo cronograma vira quadro Kanban. Arraste um cartão para mudar o andamento da tarefa.',
      },
      saveBaseline: {
        title: 'Salve a linha de base',
        body: 'A partir daqui, todo atraso ou adiantamento é medido contra este plano. É assim que seu status report tem dados, não achismo.',
      },
      premiumTeaser: {
        title: 'Acompanhe a execução',
        body: 'Veja desvio de prazo, índice de desempenho de prazo (SPI) e curva S no Portfólio.',
        cta: 'Conhecer o plano Professional',
      },
    },
  },

  checklist: {
    title: 'Primeiros Passos',
    doneCount: '{{done}} de {{total}} concluídos',
    allDone: 'Tudo pronto, você está com o jeito da ferramenta.',
    restartTour: 'Refazer o tour',
    completed: 'Concluído',
    minimize: 'Minimizar',
    close: 'Fechar',
    ariaLauncher: 'Primeiros Passos, {{done}} de {{total}} concluídos',
    groups: {
      Planejamento: 'Planejamento',
      'Execução': 'Execução',
      'Monitoramento e Controle': 'Monitoramento e Controle',
    },
    items: {
      create_project: 'Criar um projeto a partir de um modelo',
      edit_task: 'Editar uma tarefa',
      add_subtask: 'Criar uma subtarefa',
      add_dependency: 'Criar uma dependência',
      add_member: 'Cadastrar alguém na equipe',
      assign_owner: 'Atribuir um responsável',
      move_kanban: 'Mover um cartão no Kanban',
      create_pendencia: 'Registrar uma pendência',
      set_raci: 'Definir um papel na matriz RACI',
      save_baseline: 'Salvar a linha de base',
      view_critical_path: 'Ver o caminho crítico',
      create_risk: 'Registrar um risco',
      create_change: 'Abrir uma solicitação de mudança',
      create_cost: 'Lançar um custo',
      view_portfolio: 'Abrir a visão de portfólio',
    },
  },
};

/** en e es derivam desta forma: chave faltando vira erro de compilação. */
export type OnboardingCopy = typeof onboarding;

export default onboarding;
